import { useEffect, useState, useCallback, useRef } from 'react';
import './styles.scss';
import { useSelector, useDispatch } from 'react-redux';
import { PlusIcon, FadersHorizontalIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { updateHeader } from '../../slices/header-slice';
import { updateBasesData } from '../../slices/app-slice';
import { getBases } from '../../api';
import { useDataLoading } from '../../hooks/useDataLoading';

import { LazyLoadedGroup } from '../../components/lazy-loaded-group';
import { groupArrayInChunks, createSlug } from '../../utility/utils';
import FilterOverlay from '../../components/filter-overlay';


const Bases = ({ type }) => {
    const navigate = useNavigate();
    const bases = useSelector((state) => state.app.bases);
    const announcement = useSelector((state) => state.app.homepage?.announcement);
    const dispatch = useDispatch();

    const [filters, setFilters] = useState([]);
    const [activeFilter, setActiveFilter] = useState(null);
    const [activeType, setActiveType] = useState(null);
    const [filterResults, setFilterResults] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);
    const [priceRange, setPriceRange] = useState([0, 1000000]);
    const [sortOption, setSortOption] = useState(null);

    const [isBasesLoading, setIsBasesLoading] = useState(false);
    const hasFetched = useRef(false);

    const sliderRef = useRef(null);

    useDataLoading('bases', isBasesLoading, []);

    useEffect(() => {
        const fetchBasesData = async () => {
            const hasData = bases && Array.isArray(bases) && bases.length > 0;

            if (!hasData && !hasFetched.current) {
                hasFetched.current = true;
                setIsBasesLoading(true);
                try {
                    const response = await getBases();
                    dispatch(updateBasesData(response.data));
                } catch (error) {
                    console.error('Error fetching bases data:', error);
                } finally {
                    setIsBasesLoading(false);
                }
            }
        };

        fetchBasesData();
    }, [bases, dispatch]);

    const minPrice = bases && bases.length > 0 ? Math.min(...bases.filter(p => p?.startingPrice !== undefined && p?.startingPrice !== null).map(p => Math.floor(p.startingPrice || 0))) : 0;
    const maxPrice = bases && bases.length > 0 ? Math.max(...bases.filter(p => p?.startingPrice !== undefined && p?.startingPrice !== null).map(p => p.startingPrice || 0)) : 1000000;

    // Build filter configuration for FilterOverlay
    const filterConfig = {
        material: {
            label: 'Material',
            type: 'radio',
            options: filters.map(filter => ({
                value: filter,
                label: filter
            }))
        }, 
        type: {
            label: 'Types',
            type: 'radio',
            options: [
                { value: 'Coffee Table', label: 'Coffee Table' },
                { value: 'Console Table', label: 'Console Table' },
                { value: 'Dining Table', label: 'Dining Table' }
            ]
        },
        price: {
            label: 'Price',
            type: 'price'
        }
    };

    const sortConfig = [
        { value: 'highToLow', label: 'High to Low' },
        { value: 'lowToHigh', label: 'Low to High' }
    ];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Set filters and initialize results
    useEffect(() => {
        dispatch(updateHeader('light'));

        if (bases && Array.isArray(bases) && bases.length > 0) {
            const categories = bases.filter(b => b?.material !== null && b?.material !== undefined).map(x => x.material);
            const unique = Array.from(new Set(categories));
            setFilters(unique);
            setFilterResults(groupArrayInChunks(bases, 8));
        }
    }, [dispatch, bases, type]);

    useEffect(() => {
        if (!bases || !Array.isArray(bases) || bases.length === 0) {
            setFilterResults([]);
            return;
        }

        let result = [...bases];

        if (activeFilter) {
            result = result.filter(x => x?.material === activeFilter);
        }

        if (activeType) {
            result = result.filter(x => x?.type === activeType);
        }

        // Filter by price, but include bases without startingPrice (they might be custom/contact for pricing)
        result = result.filter(x => {
            if (x?.startingPrice === undefined || x?.startingPrice === null) {
                return true;
            }
            return x.startingPrice >= priceRange[0] && x.startingPrice <= priceRange[1];
        });

        // Apply sorting after filtering
        if (sortOption) {
            if (sortOption === 'highToLow') {
                result.sort((a, b) => {
                    const priceA = a.startingPrice || 0;
                    const priceB = b.startingPrice || 0;
                    return priceB - priceA;
                });
            } else if (sortOption === 'lowToHigh') {
                result.sort((a, b) => {
                    const priceA = a.startingPrice || 0;
                    const priceB = b.startingPrice || 0;
                    return priceA - priceB;
                });
            }
        }

        setFilterResults(groupArrayInChunks(result, 8));
    }, [activeFilter, activeType, bases, priceRange, sortOption]);


    const handleFilterApply = useCallback((appliedFilters, appliedSort) => {
        setActiveFilter(appliedFilters.material || null);
        setActiveType(appliedFilters.type || null);
        setSortOption(appliedSort);
    }, []);

    const onProductClick = (item) => {
        if (!item?.id || !item?.title) return;

        const titleSlug = createSlug(item.title);
        navigate({
            pathname: `/bases/${item.id}`,
            search: `?title=${titleSlug}`,
        });
    };

    const handlePriceChange = (range) => {
        setPriceRange(range);
    };

    return (
        <div className={`bases-page ${announcement ? 'has-announcement' : ''}`}>
            <div className="store-intro">
                <h1>Base Designs.</h1>
                <p>All bases designs are proprietary designs & can only be found here.</p>
                <p>Looking for something truly one of a kind? <a className='text-link' href="/contact">Send us a message</a> & our designers can create a never before seen base just for you</p>
            </div>

            {/* <div className="base-type-filter">
                <div className="base-filter info">
                    <div>
                        <h2>Base Type</h2>
                        <p>Select to filter design choice.</p>
                    </div>
                </div>
                {['Dining Table', 'Coffee Table', 'Console Table'].map((type) => (
                    <div
                        key={type}
                        className={`base-filter ${activeBaseFilter === type ? "active" : ""}`}
                        onClick={() => setActiveBaseFilter(activeBaseFilter === type ? null : type)}
                    >
                        <img src={
                            type === 'Dining Table' ? baseFilterDining :
                                type === 'Coffee Table' ? baseFilterCoffee :
                                    baseFilterConsole
                        } alt={`${type} Filter`} />

                    </div>
                ))}
            </div> */}

            <div className="tools-wrap">
                <div className="tools">
                    <div className="filter" onClick={() => setFilterOpen(!filterOpen)}>
                        <span><span> Filters <PlusIcon size={16} /> </span><span>Sort <FadersHorizontalIcon size={24} /></span></span>
                    </div>
                </div>
            </div>

            <FilterOverlay
                isOpen={filterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleFilterApply}
                filters={filterConfig}
                activeFilters={{
                    material: activeFilter,
                    type: activeType
                }}
                sortOptions={sortConfig}
                activeSortOption={sortOption}
                priceRange={priceRange}
                minPrice={minPrice}
                maxPrice={maxPrice}
                onPriceChange={handlePriceChange}
                priceSliderRef={sliderRef}
                title="BASES"
            />

            <div className="lazy-load">
                {filterResults.map((group, i) => (
                    <LazyLoadedGroup
                        key={i}
                        group={group}
                        index={i}
                        type={type}
                        onProductClick={onProductClick}
                    />
                ))}
            </div>
        </div>
    );
};

export default Bases;
